import { getDataReaderClient } from "@multiweb/clients/util";
import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { CancellationTokenSource } from "@multiweb/types/multiweb";
import { CaseInsensitiveMap } from "Global/Base/DataTypes/CaseInsensitiveMap";
import { NotImplementedException } from "Global/Base/Exceptions";
import { PromiseUtils } from "Global/Base/Promises/PromiseUtils";
import { StringUtils } from "Global/Base/Utils/StringUtils";
import { EOWRecordPermissionException } from "OW.Remoting/Basic/OWExceptions";
import { IOWObject } from "OW.Remoting/Remoting/DataTypes/IOWObject";
import { RequestContext } from "../../../../OW.Remoting/Remoting/RequestContext";
import { FrameContext } from "../../../UI/UI/UIManagerInterfaces";
import { convertContextFromFrameToRequest } from "../../../UI/Utilities/SharedUIMethods";
import { MEOWObject } from "./MEOWObject";
import { IMEChangeGroup, IMEChangeStack, IMEConditionGroup, IMEInstanceChangeProp, IMERecordsGroup, IntersectResult, MEAttrChange, MEChanges } from "./types";

const logger = LazyLoadingSectionLogger.create(LoggingSection.multiEditing);

/**
 * Třída, která agreguje změny vybraných skupin a poskytuje aktuální data pro zobrazení na formuláři
 * Po napojení na datasource by mohla poskytovat IOWObject
 */
export class MEChangeStack implements IMEChangeStack {
    private _changesCount: number = 0;
    private _objectIds: CaseInsensitiveMap<MEAttrChange[]> = new CaseInsensitiveMap<MEAttrChange[]>();
    private _groups: IMEChangeGroup[] = [];

    private _cancellationTokenSource?: CancellationTokenSource;
    /** Funkce, která mapuje ID objektu na instanceID */
    private _instanceIdGetter: (id: string) => string;
    private _frameContext: FrameContext | undefined = undefined;
    private readonly _composites: CaseInsensitiveMap<string>;

    /** Třída pro kterou se změny budou aplikovat */
    public readonly className: string;
    /** Slovník jmen povinných atributů s id podmínek povinnosti */
    public requiredAttrs: Map<string, number>;

    constructor(className: string, instanceIdGetter: (id: string) => string, frameContext: FrameContext | undefined, composites?: string | undefined) {
        this.className = className;
        this._instanceIdGetter = instanceIdGetter;
        this._frameContext = frameContext;
        this._composites = this._parseCompositeAttrClasses(composites);
    }

    private _parseCompositeAttrClasses(value: string): CaseInsensitiveMap<string> {
        if (value)
            return new CaseInsensitiveMap(value.split(/\s*;\s*/).map(v => v.split(/\s*=\s*/) as [string, string]));
        return new CaseInsensitiveMap<string>();
    }

    /** Seznam skupin se změnami, které nejsou aplikovány instančně */
    public get groups(): readonly IMEChangeGroup[] {
        return this._groups;
    }

    /** Instance pro které se aplikují změny přímo */
    public get instances(): IMEInstanceChangeProp[] {
        const ret: IMEInstanceChangeProp[] = [];
        this._objectIds?.forEach((v, k) => {
            const iid = this._instanceIdGetter(k);
            if (!iid) {
                throw new Error(`[MultiEdit] InstanceID for ID '${k}' was not found.`);
            }
            ret.push({ id: k, instanceId: iid, changes: v });
        });
        return ret;
    }

    /**
     * Do existující instance přidá nové změny a zachová pořadí.
     * Pokud je nová změna shodná s předchozí, nepřidá se.
     * @param objId ID instance
     * @param changes Seznam nových změn
     */
    private _updateInstance(objId: string, changes: readonly MEAttrChange[]): void {
        if (!this._objectIds.has(objId)) {
            this._objectIds.set(objId, []);
        }
        const instanceChanges = this._objectIds.get(objId);
        changes.forEach(i => {
            const order = instanceChanges.length;
            if (order > 0) {
                const { attrName, value } = instanceChanges[order - 1];
                if (StringUtils.compareCaseInsensitive(attrName, i.attrName) && value === i.value) {
                    return;
                }
            }
            instanceChanges.push({ ...i, order });
        });
    }

    /** Pro zadanou skupinu udělá průnik s existujícími skupinami a vrací seznam instancí v průniku a její skupinu */
    private async _intersects(group: IMEChangeGroup, requestContext: RequestContext): Promise<IntersectResult[]> {
        const results: IntersectResult[] = [];
        for (const g of this._groups) {
            const interResult = await this._instersectsGroup(group, g, requestContext);
            if (interResult) {
                interResult.forEach(i => results.push({ id: i, group: g }));
            }
        }
        return results.length ? results : undefined;
    }

    /**
     * Vrací seznam instancí, které jsou průnikem dvou zadaných skupin
     */
    private async _instersectsGroup(a: IMEChangeGroup, b: IMEChangeGroup, requestContext: RequestContext): Promise<string[] | undefined> {
        if (a instanceof IMERecordsGroup && b instanceof IMERecordsGroup) {
            return this._intersectInstances(a, b);
        } else if (a instanceof IMEConditionGroup && b instanceof IMEConditionGroup) {
            return await this._intersectConditions(a.condition, b.condition, requestContext);
        } else {
            if (a instanceof IMEConditionGroup && b instanceof IMERecordsGroup) {
                return await this._intersectInstancesWithCondition(a, b, requestContext);
            }
            if (a instanceof IMERecordsGroup && b instanceof IMEConditionGroup) {
                return await this._intersectInstancesWithCondition(b, a, requestContext);
            } else {
                throw new NotImplementedException();
            }
        }
    }

    /** Vrací průnik dvou skupin zadaných seznamem instancí (klient)*/
    private _intersectInstances(a: IMERecordsGroup, b: IMERecordsGroup): string[] {
        return a.instances.filter(i => b.instances.includes(i));
    }

    /** Vrací průnik skupiny zadané podmínkou a skupiny zadané instancí (server) */
    private async _intersectInstancesWithCondition(a: IMEConditionGroup, b: IMERecordsGroup, requestContext: RequestContext): Promise<string[]> {
        const validIds = b.instances.filter(b => !a.excluded.includes(b));
        //pokud není žádné ID, které by bylo potřeba porovnat s podmínkou můžu rovnou vrátit prázdný průnik
        if (validIds?.length === 0)
            return [];
        return await this._intersectIdsWithCondition(a.condition, validIds, requestContext);
    }

    /** Vrací seznam instancí, které mají průnik se zadanou podmínkou (server) */
    private async _intersectIdsWithCondition(groupCondition: string, objIds: string[], requestContext: RequestContext): Promise<string[]> {
        if (!objIds || objIds.length === 0)
            return [];
        const condition = `(${groupCondition}) AND (ID IN [${objIds.map(i => "'" + i + "'").join(",")}])`;
        const res = await getDataReaderClient().getDataRowsWithParams(this.className, ["ID"], condition, undefined, undefined, requestContext);
        if (res) {
            return res.map(i => i["ID"].toString());
        } else {
            logger.error(`Something went wrong for condition '${condition}'. Data reader should return at least empty rows.`);
            return [];
        }
    }

    /** Vrací seznam instancí, které jsou průnikem dvou podmínek (server) */
    private async _intersectConditions(condition1: string, condition2: string, requestContext: RequestContext): Promise<string[]> {
        const condition = `(${condition1}) AND (${condition2})`;
        const res = await getDataReaderClient().getDataRowsWithParams(this.className, ["ID"], condition, undefined, undefined, requestContext);
        if (res) {
            return res.map(i => i["ID"].toString());
        } else {
            logger.error(`Something went wrong for condition '${condition}'. Data reader should return at least empty rows.`);
            return [];
        }
    }

    /** Pro zadanou instanci vrací seznam aktuálně evidovaných instančních změn */
    public getChangesForObject(objId: string): readonly MEAttrChange[] | undefined {
        if (this._objectIds.has(objId))
            return this._objectIds.get(objId);
        return undefined;
    }

    /** Vyprázní zásobník */
    public clear(): void {
        this._changesCount = 0;
        this._objectIds.clear();
        this._groups = [];
    }

    /**
     * Vrací aktuální reprezentaci objektu pro zobrazení na formuláři
     * Momentálně vrací mapu, při realizace Datasource by měl vrace IOWObject
    */
    public getCurrentObject(currentGroup: IMEChangeGroup, singleObjectGetter: () => Promise<IOWObject>): Promise<IOWObject | undefined> {
        this._cancellationTokenSource?.cancel();
        this._cancellationTokenSource = new CancellationTokenSource();

        const currentObjectDeferred = PromiseUtils.defer<IOWObject>();

        this._createCurrentObject(currentGroup, singleObjectGetter, convertContextFromFrameToRequest(this._frameContext, this._cancellationTokenSource.token))
            .then(obj => currentObjectDeferred.resolve(obj))
            .catch(PromiseUtils.cancellationCatch)
            .catch(e => {
                logger.error("failed to add selected group to stack.", e);
                currentObjectDeferred.reject(e);
            });

        //vracím deffered promise schválně, při rychlém překlikání se bude rychle za sebou volat getCurrentObject(),
        //předchozí promise tak můžeme ihned rejectnout a nemusí se čekat až skutečně doběhne
        return currentObjectDeferred.promise;
    }

    /**
     * Přidá novou skupinu změn.
     * Volání by mělo proběhnout při změně výběru. Při vstupu do multi editace se založí skupina a postupně se do ní ukládají změny.
     * Při změně výběru nebo uložení se skupina přidá aplikují se nové změny. Vytváří se reprezentace aktuálního objektu,
     * která nastaví deffered promise na actualObject a teprve až je actual objekt připraven, resolvne tento promis
     */
    public async add(group: IMEChangeGroup, requestContext: RequestContext): Promise<void> {
        try {
            let merged: MEChanges;
            if (group instanceof IMEConditionGroup) {
                const intersectsInstances = group instanceof IMEConditionGroup ? await this._intersectIdsWithCondition(group.condition, this.instances.map(i => i.id), requestContext) : [];
                merged = group.merge(Array.from(this._objectIds.keys()), intersectsInstances);
            } else if (group instanceof IMERecordsGroup) {
                const intersectsGroups = await this._intersects(group, requestContext);
                merged = group.merge(Array.from(this._objectIds.keys()), intersectsGroups);
            } else {
                throw new Error("[MultiEdit] Unknown group type");
            }

            merged.changedInstances?.forEach(i => this._updateInstance(i.id, i.values));
            merged.newGroups?.forEach(g => this._groups.push(g));

            if (merged?.changedInstances) {
                merged.changedInstances.forEach(i => logger.debug(`Added instance changes for '${i.id}'.`, i.values));
            }
            if (merged?.newGroups) {
                for (const g of merged.newGroups) {
                    if (g instanceof IMEConditionGroup)
                        logger.debug(`Added group changes for '${g.condition}'`, g.changes);
                    else if (g instanceof IMERecordsGroup)
                        logger.debug(`Added group changes for [${g.instances.join(",")}]`, g.changes);
                }
            }

            if (merged.remGroups?.length) {
                logger.debug("Removing groups", merged.remGroups);
                this._groups = this._groups.filter(g => !merged.remGroups.includes(g));
            }
            this._changesCount = 0;
            this._objectIds.forEach(changes => this._changesCount += changes.length);
        } catch (e) {
            logger.error("failed to add selection group to stack.", e);
        }
    }

    private async _createCurrentObject(currentGroup: IMEChangeGroup, singleObjectGetter: () => Promise<IOWObject>, requestContext: RequestContext): Promise<IOWObject | null> {
        /*
        Tahle metoda by měla ve finále vracet IOWObject, který by vracel DataSource. Možná se bude muset implementovat nový IOWObjekt pro
        tyhle účely. Určitě by měl podporovat setValue, které by mělo promítat změny do aktualně vybrané groupy.

          * Pokud bude vybrán jeden objekt, tak by se měl vrátit skutečný OWObjekt, nad kterým se aplikují změny. V GUI budou zobrazeny
            konkrétní hodnoty u všech atributů.
          * Pokud bude označno dva a více objektů, měl by se vrátit pseudo OWObjekt, kde budou nasetovaný jen hodnoty atributů,
            které jsou společny pro všechny aktuálně vybrané objekty. V GUI pak budou mít ty atributy u kterých víme že mají stejnou
            hodnotu zobrazenou konkrétní hodnotu. U ostatních by mělo být, že se hodnota může lišit
          * Pokud bude vybráno vše (skupina zadaná podmínkou), tak se vrací IOWObject, který nebude mít nasetované žádné atributy,
            v GUI by pak u všech těchto atributů mělo být, že se hodnota může lišit.
        */
        let obj: IOWObject = null;
        const intersectsGroups = await this._intersects(currentGroup, requestContext) || [];
        if (currentGroup instanceof IMERecordsGroup && (currentGroup.instances.length === 0 || currentGroup.instances.length === 1)) {
            try {
                obj = await singleObjectGetter();
            } catch (error) {
                logger.error("Unable to load single object", error);

                if (!(error instanceof EOWRecordPermissionException)) {
                    throw error;
                }
            }

            const currentGroupInstanceId = currentGroup.instances.length === 1 ? currentGroup.instances[0] : null;
            if (currentGroupInstanceId && currentGroupInstanceId === obj?.instanceID) {
                if (this._objectIds.has(obj?.id?.toString())) {
                    const uniqueChanges = MEChangeStack._findLatestAttributeChanges(this._objectIds.get(obj.id.toString()));
                    for (const a of uniqueChanges) {
                        logger.debug("Restoring change", a.attrName, a.value);
                        await obj.setValue(a.attrName, a.value);
                    }
                }
                for (const ig of intersectsGroups) {
                    for (const change of ig.group.changes) {
                        await obj.setValue(change.attrName, change);
                    }
                }
            }
        } else {
            obj = await MEOWObject.createObject(this._frameContext, this.className, this._composites);
        }

        this.print();

        obj ? logger.debug("Created object", obj) : logger.debug("Couldn't create object, returning: ", obj);

        return obj;
    }

    public print(): void {
        logger.groupCollapsed(`► Changes stack (${this._changesCount} changes)`);
        //instances
        logger.groupCollapsed(`► Instances (${this.instances.length})`);
        for (const i of this.instances) {
            logger.groupCollapsed(`► ${i.id} (${i.instanceId})(${i.changes.length} changes)`);
            for (const ch of i.changes) {
                logger.debug(ch.order, ch.attrName, ch.value?.valueOf());
            }
            logger.groupEnd();
        }
        logger.groupEnd();
        //groups
        logger.groupCollapsed(`► Groups (${this.groups.length})`);
        for (const g of this.groups) {
            const except = g.excluded?.length ? ` EXCEPT [${g.excluded.join(",")}]` : "";
            if (g instanceof IMEConditionGroup)
                logger.groupCollapsed(`► ${g.condition}${except} (${g.changes.length} changes)`);
            else if (g instanceof IMERecordsGroup)
                logger.groupCollapsed(`► [${g.instances.join(",")}]${except} (${g.changes.length} changes)`);
            for (const ch of g.changes) {
                logger.debug(ch.order, ch.attrName, ch.value?.valueOf());
            }
            logger.groupEnd();
        }
        logger.groupEnd();
        logger.groupEnd();
    }

    /**
     * Pro každý atribut ze seznamu změn najde poslední změnu a přidá ji do výsledného seznamu.
     *
     * @private
     * @returns Seznam změn, kde každý atribut má právě jednu změnu.
     * @param orderedChanges Seznam všech změn, seřazen vzestupně podle pořadí {@link MEAttrChange.order|order}.
     */
    private static _findLatestAttributeChanges(orderedChanges: readonly MEAttrChange[]): MEAttrChange[] {
        const uniqueAttributeChanges = orderedChanges.reduceRight((acc, change) => {
            if (!acc[change.attrName]) {
                acc[change.attrName] = change;
            }
            return acc;
        }, {} as Record<string, MEAttrChange | undefined>);
        return Object.values(uniqueAttributeChanges);
    }
}
